import {
  CSSProperties,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { clsx } from 'clsx'
import { createPortal } from 'react-dom'

import { getDomainInfo } from '@tribeplatform/react-components/common/utils'
import {
  getIconPickerSpriteUrl,
  getIconSocialSpriteUrl,
  getIconSpriteUrl,
} from '@tribeplatform/react-components/Picker'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { DocumentContext } from '@tribeplatform/react-ui-kit/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import {
  ThemeProvider,
  TYPOGRAPHY_LINK_ID,
  TYPOGRAPHY_STYLE_ID,
  THEME_STYLE_ID,
  ColorSchemeProvider,
} from '@tribeplatform/react-ui-kit/ThemeProvider'

import { getAssetPath } from '../../../../../../configs/static.js'
import { injectHTMLElement } from '../../../../utils/html.injector.js'
import { useCustomizerTheme } from '../hooks/index.js'

type IFrameProps = {
  children: ReactNode
  className?: string
  style?: CSSProperties
  darkMode: boolean
}

export const IFrame: FC<IFrameProps> = ({
  darkMode,
  children,
  className,
  style,
}) => {
  const {
    data: { network },
  } = useAuthToken()
  const { subfolder } = getDomainInfo({
    domain: network?.domain,
    domainSubfolder: network?.domainSubfolder,
  })

  const [mountedWithStyle, setMountedWithStyle] = useState<boolean>(false)
  const [frameLoaded, setFrameLoaded] = useState<boolean>(false)
  const [contentRef, setContentRef] = useState<HTMLIFrameElement>(null)
  const mountDocument = contentRef?.contentWindow?.document
  const { appearance } = useCustomizerTheme()

  // init emojis
  useEffect(() => {
    if (mountDocument) {
      const emojiMartScript = document.createElement('script')
      emojiMartScript.type = 'text/javascript'
      emojiMartScript.src =
        'https://cdn.jsdelivr.net/npm/emoji-mart@5.2.1/dist/browser.js'

      injectHTMLElement({
        document: mountDocument,
        element: emojiMartScript,
        position: 'body',
      })

      const iframelyScript = document.createElement('script')
      iframelyScript.type = 'text/javascript'
      iframelyScript.src = '//cdn.iframe.ly/embed.js'

      injectHTMLElement({
        document: mountDocument,
        element: iframelyScript,
        position: 'body',
      })

      const emojiInitScript = document.createElement('script')
      emojiInitScript.type = 'text/javascript'
      emojiInitScript.defer = true
      // for some reason, EmojiMart.init() does not work but adding invisible Picker does
      emojiInitScript.text = `
      const initEmojis = () => {
        const div = document.createElement("div")
        div.id = "emoji-mart-picker"
        div.style.display = "none"

        const picker = new EmojiMart.Picker({
          parent: document.querySelector('#emoji-mart-picker'),
        })
        div.append(picker)
        document.body.appendChild(div)
      }

      window.onload = function () {
        initEmojis()
      }
      setTimeout(() => {
        initEmojis()
      }, 1000)
      `

      injectHTMLElement({
        document: mountDocument,
        element: emojiInitScript,
        position: 'body',
      })
    }
  }, [mountDocument])

  // copy all the links and styles (tailwind for example) from the root document into the iframe
  useEffect(() => {
    if (mountDocument) {
      document.querySelectorAll('style').forEach(e => {
        if (e.id !== TYPOGRAPHY_STYLE_ID && e.id !== THEME_STYLE_ID) {
          injectHTMLElement({ document: mountDocument, element: e })
        }
      })
      // [rel=stylesheet] was added to the query selector to address a bug where Edge browser was crashing in the customizer
      document
        .querySelectorAll('link[rel=stylesheet]')
        .forEach((e: HTMLLinkElement) => {
          if (e.id !== TYPOGRAPHY_LINK_ID) {
            injectHTMLElement({ document: mountDocument, element: e })
          }
        })
      const handler = setTimeout(() => {
        setMountedWithStyle(true)
      }, 1000)

      return () => {
        clearTimeout(handler)
      }
    }
  }, [mountDocument])

  const assets = useMemo(
    () => ({
      iconSpriteUrl: getIconSpriteUrl(appearance.style.icons, {
        subfolder,
      }),
      iconSocialSpriteUrl: getIconSocialSpriteUrl({ subfolder }),
      iconPickerLineSpriteUrl: getIconPickerSpriteUrl('line', {
        subfolder,
      }),
      iconPickerSolidSpriteUrl: getIconPickerSpriteUrl('solid', {
        subfolder,
      }),
      iconPickerDuocolorSpriteUrl: getIconPickerSpriteUrl('duocolor', {
        subfolder,
      }),
    }),
    [appearance.style.icons, subfolder],
  )

  return (
    <div style={style} className="relative mx-auto">
      {!mountedWithStyle && (
        <div className="absolute inset-0 bg-background flex items-center justify-center rounded-base">
          <SvgIcon className="animate-spin h-7 w-7" name="spinner" />
        </div>
      )}
      <iframe
        title="tribe-device-view"
        ref={setContentRef}
        className={clsx(
          className,
          'transition-opacity duration-300',
          frameLoaded && mountedWithStyle ? 'opacity-100' : 'opacity-0',
        )}
        onLoad={() => setFrameLoaded(true)}
      >
        {mountDocument &&
          createPortal(
            <DocumentContext.Provider value={{ document: mountDocument }}>
              <ColorSchemeProvider
                colorSchemeMode={darkMode ? 'dark' : 'light'}
              >
                <ThemeProvider
                  theme={appearance.theme}
                  typography={appearance.typography}
                  style={appearance.style}
                  networkColorScheme={appearance.networkColorScheme}
                  assets={assets}
                >
                  {children}
                  {/* We had to add a dummy image and make sure that it loads in order to ensure iframe onload gets fired */}
                  <img
                    className="hidden"
                    src={getAssetPath('images/default-icon.svg')}
                    alt=""
                  />
                </ThemeProvider>
              </ColorSchemeProvider>
            </DocumentContext.Provider>,
            mountDocument.body,
          )}
      </iframe>
    </div>
  )
}
